import React, { useState, useEffect, useMemo } from "react";
import { getApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import QuestionInput from "./QuestionInput";
import { GuidanceBlock, evalCondition, TRIP_SEP } from "./displayConditionUtils";
 
export const useTableDefinition = (tableRowTypeId) => {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(false);
 
  useEffect(() => {
    if (tableRowTypeId && typeof tableRowTypeId === "string") {
      const load = async () => {
        setLoading(true);
        try {
          const res = await getApi({
            apiUrl: API_URLS.GET_TABLE_ROW_TYPE_DEFINITION(tableRowTypeId),
          });
          if (res && res.success) {
            setDefinition(res.data);
          }
        } catch (err) {
          console.error(`Failed table definition load ${tableRowTypeId}`, err);
        }
        setLoading(false);
      };
      load();
    }
  }, [tableRowTypeId]);
  return { definition, loading };
};
 
const FieldTitle = ({ fieldTitle }) => {
  const titles = Array.isArray(fieldTitle)
    ? fieldTitle
    : fieldTitle
      ? [fieldTitle]
      : [];
  const visibleTitles = titles.filter(
    (title) => title !== undefined && title !== null && title !== "",
  );
 
  if (visibleTitles.length === 0) return null;
 
  return (
    <div className="mb-2 mt-4 space-y-1">
      {visibleTitles.map((title, index) => (
        <p
          key={`${String(title)}_${index}`}
          className="block text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1"
        >
          {title}
        </p>
      ))}
    </div>
  );
};
 
const QuestionTable = ({ question, tableId, answers, setAnswers }) => {
  const { definition, loading } = useTableDefinition(question.tableRowTypeId);
 
  const fields = definition?.fields || [];
 
  // 1. Identify Seed Fields (Triggers categorization)
  const seedFields = useMemo(
    () =>
      fields.filter(
        (f) =>
          f.fieldType === "multiselect" &&
          (!f.displayCondition || f.displayCondition.length === 0) &&
          fields.some((child) =>
            child.displayCondition?.some(
              (c) => (c.triggerQuestionId || c.triggerFieldId) === f.fieldId
            )
          )
      ),
    [fields],
  );
 
  const isCategorized = seedFields.length > 0;
 
  // 2. Identify Global Fields vs Categorized Fields
  // Global: No display conditions and not a seed field.
  // Categorized: Has display conditions.
  const { globalFields, categorizedFields } = useMemo(() => {
    const g = [];
    const c = [];
    fields.forEach((f) => {
      // If it's a seed field, it's a selector, usually rendered with globals.
      if (seedFields.find((sf) => sf.fieldId === f.fieldId)) return;
 
      if (!f.displayCondition || f.displayCondition.length === 0) {
        g.push(f);
      } else {
        c.push(f);
      }
    });
    return { globalFields: g, categorizedFields: c };
  }, [fields, seedFields]);
 
  // 3. Resolve selected categories from all seed fields
  const selectedCategories = useMemo(() => {
    if (!isCategorized) return ["default"];
    const allCats = new Set();
    seedFields.forEach((cf) => {
      const vals = answers[`${tableId}_${cf.fieldId}`] || [];
      vals.forEach((v) => { if (v) allCats.add(v); });
    });
    return Array.from(allCats);
  }, [seedFields, answers, tableId, isCategorized]);
 
  // 4. Initialize State
  useEffect(() => {
    if (!definition || loading) return;
    setAnswers((prev) => {
      const tableData = prev[tableId] || {};
      let changed = false;
      const newTableData = { ...tableData };
 
      // Ensure 'default' always exists (for global and non-categorized fields)
      if (!newTableData["default"] || newTableData["default"].length === 0) {
        newTableData["default"] = [{ rowId: "row_1", values: {} }];
        changed = true;
      }
 
      // Ensure each selected category has its row instance
      if (isCategorized) {
        selectedCategories.forEach((catKey) => {
          if (!newTableData[catKey] || newTableData[catKey].length === 0) {
            newTableData[catKey] = [{ rowId: "row_1", values: {} }];
            changed = true;
          }
        });
      }
 
      if (changed) return { ...prev, [tableId]: newTableData };
      return prev;
    });
  }, [
    tableId,
    selectedCategories.join(","),
    definition,
    loading,
    setAnswers,
    isCategorized,
  ]);
 
  const setTableRow = (catKey, rowId, rowData) => {
    let updatedRowData = { ...rowData };
    const calculationGroups = {};
 
    fields.forEach((field) => {
      if (field.resultAction) {
        const action = field.resultAction;
        const sourceFields = fields.filter(
          (f) =>
            f.calculationType &&
            Array.isArray(f.calculationType) &&
            f.calculationType.includes(action),
        );
 
        if (sourceFields.length > 0) {
          if (!calculationGroups[action]) {
            calculationGroups[action] = [];
          }
          calculationGroups[action].push({
            targetField: field.fieldId,
            sourceFields: sourceFields.map((f) => f.fieldId),
            action: action,
          });
        }
      }
    });
 
    Object.values(calculationGroups).forEach((calculations) => {
      calculations.forEach(({ targetField, sourceFields, action }) => {
        let total = 0;
        let count = 0;
        let hasData = false;
 
        sourceFields.forEach((sourceId) => {
          const value = updatedRowData[sourceId];
          if (value !== undefined && value !== null && value !== "") {
            const numValue = parseFloat(value) || 0;
            total += numValue;
            count++;
            hasData = true;
          }
        });
 
        if (hasData) {
          if (action === "total") {
            updatedRowData[targetField] = total;
          } else if (action === "avg") {
            updatedRowData[targetField] =
              count > 0 ? Number((total / count).toFixed(4)) : 0;
          }
        } else {
          updatedRowData[targetField] = "";
        }
      });
    });
    setAnswers((prev) => {
      const tableData = prev[tableId] || {};
      const groupRows = tableData[catKey] || [];
      const idx = groupRows.findIndex((r) => r.rowId === rowId);
      const updated = [...groupRows];
      if (idx >= 0) updated[idx] = { ...updated[idx], values: updatedRowData };
      else updated.push({ rowId, values: updatedRowData });
      return { ...prev, [tableId]: { ...tableData, [catKey]: updated } };
    });
  };
 
  if (loading)
    return (
      <div className="p-4 bg-gray-50 border rounded animate-pulse">
        Loading Table...
      </div>
    );
  if (!definition) return null;
 
  const tableData = answers[tableId] || {};
 
  return (
    <div className="space-y-6">
      {/* SECTION 2: Global Table Fields (rendered once at the top) */}
      {(globalFields.length > 0 || !isCategorized) && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-4">
          {(tableData["default"] || []).map((row) => (
            <div key={row.rowId} className="space-y-4">
              {(() => {
                const contextFields = isCategorized ? globalFields : fields;
                const visibleFields = contextFields.filter(f => evalCondition(f, answers, { rowData: row.values }));
 
                const branchingTriggers = contextFields.filter(
                  (f) =>
                    (f.fieldType === "number" || f.fieldType === "multiselect") &&
                    contextFields.some((child) =>
                      child.displayCondition?.some(
                        (c) =>
                          (c.triggerQuestionId || c.triggerFieldId) === f.fieldId &&
                          c.operator === "exists"
                      )
                    )
                );
 
                const allDependents = new Set();
                const dependentsMap = new Map();
 
                branchingTriggers.forEach((bt) => {
                  const deps = contextFields.filter((child) =>
                    child.displayCondition?.some(
                      (c) =>
                        (c.triggerQuestionId || c.triggerFieldId) === bt.fieldId &&
                        c.operator === "exists"
                    )
                  );
                  dependentsMap.set(bt.fieldId, deps);
                  deps.forEach((d) => allDependents.add(d.fieldId));
                });
 
                const renderedFields = [];
                visibleFields.forEach((field) => {
                  if (allDependents.has(field.fieldId)) return;
 
                  renderedFields.push(
                    <div key={field.fieldId}>
                      <FieldTitle fieldTitle={field.fieldTitle} />
                      <GuidanceBlock guidance={field.guidance} />
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {field.fieldLabel}
                      </label>
                      <QuestionInput
                        field={field}
                        value={row.values[field.fieldId]}
                        disabled={[
                          "total_annual_consumption",
                          "avg_annual_consumption",
                        ].includes(field.fieldId)}
                        onChange={(val) =>
                          setTableRow("default", row.rowId, {
                            ...row.values,
                            [field.fieldId]: val,
                          })
                        }
                      />
                    </div>
                  );
 
                  if (branchingTriggers.includes(field)) {
                    const rawValue = row.values[field.fieldId];
                    const isMultiselect = field.fieldType === "multiselect";
                    const iterations = isMultiselect
                      ? Array.isArray(rawValue)
                        ? rawValue
                        : []
                      : Array.from(
                        { length: Math.min(parseInt(rawValue) || 0, 100) },
                        (_, i) => i,
                      );
 
                    if (iterations.length > 0) {
                      const deps = dependentsMap.get(field.fieldId) || [];
                      renderedFields.push(
                        <div
                          key={`${field.fieldId}_loops`}
                          className="mt-4 space-y-4"
                        >
                          {iterations.map((iterVal, i) => {
                            const displayLabel = isMultiselect
                              ? (field.options?.find(o => o.value === iterVal)?.label || iterVal)
                              : `Entry ${i + 1}`;
 
                            return (
                              <div
                                key={`${field.fieldId}_loop_${i}`}
                                className="p-4 border border-teal-200 rounded-lg bg-teal-50/30 space-y-4"
                              >
                                <div className="text-teal-700 font-bold mb-2 uppercase text-xs tracking-wider">
                                  {displayLabel}
                                </div>
                                {deps
                                  .filter((depField) =>
                                    evalCondition(depField, answers, {
                                      rowData: row.values,
                                      tripIndex: i,
                                    }),
                                  )
                                  .map((depField) => {
                                    const suffixedId = `${depField.fieldId}${TRIP_SEP}${i}`;
                                    return (
                                      <div key={suffixedId}>
                                        <FieldTitle
                                          fieldTitle={depField.fieldTitle}
                                        />
                                        <GuidanceBlock
                                          guidance={depField.guidance}
                                        />
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                                          {depField.fieldLabel}
                                        </label>
                                        <QuestionInput
                                          field={depField}
                                          value={row.values[suffixedId]}
                                          onChange={(val) =>
                                            setTableRow("default", row.rowId, {
                                              ...row.values,
                                              [suffixedId]: val,
                                            })
                                          }
                                        />
                                      </div>
                                    );
                                  })}
                              </div>
                            );
                          })}
                        </div>,
                      );
                    }
                  }
                });
 
                return renderedFields;
              })()}
            </div>
          ))}
        </div>
      )}
 
      {/* SECTION 1: Category Triggers */}
      {isCategorized &&
        seedFields.map((cf) => {
          const rowData = { ...(tableData?.["default"]?.[0]?.values || {}) };
 
          // Inject all seed fields into rowData so interdependent seed fields can trigger each other
          seedFields.forEach(sf => {
            rowData[sf.fieldId] = answers[`${tableId}_${sf.fieldId}`] || [];
          });
 
          if (!evalCondition(cf, answers, { rowData })) return null;
 
          return (
            <div
              key={cf.fieldId}
              className="mb-4 p-4 bg-teal-50/50 border-l-4 border-teal-500 rounded-lg shadow-sm"
            >
              <FieldTitle fieldTitle={cf.fieldTitle} />
              <GuidanceBlock guidance={cf.guidance} />
              <label className="block text-[#06513D] font-bold mb-2">
                {cf.fieldLabel}
              </label>
              <QuestionInput
                field={cf}
                value={answers[`${tableId}_${cf.fieldId}`] || []}
                onChange={(val) =>
                  setAnswers((prev) => ({
                    ...prev,
                    [`${tableId}_${cf.fieldId}`]: val,
                  }))
                }
              />
            </div>
          );
        })}
 
      {/* SECTION 3: Categorized Fields Loop */}
      {isCategorized && categorizedFields.length > 0 && selectedCategories.map((catKey) => {
        const rows = tableData[catKey] || [];
        if (rows.length === 0) return null;
 
        let catLabel = catKey;
        for (const cf of seedFields) {
          const opt = cf.options?.find((o) => o.value === catKey);
          if (opt) {
            catLabel = opt.label;
            break;
          }
        }
 
        return (
          <div
            key={catKey}
            className="border-2 border-teal-50 rounded-xl p-4 bg-teal-50/10 shadow-sm"
          >
            <h3 className="text-md font-bold text-teal-700 mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
              {catLabel}
            </h3>
            {rows.map((row) => {
              const context = { ...row.values };
              // Inject the category value so evalCondition can detect the row context
              seedFields.forEach((cf) => {
                const vals = answers[`${tableId}_${cf.fieldId}`] || [];
                if (vals.includes(catKey)) context[cf.fieldId] = [catKey];
              });
 
              const visibleFieldsInCat = categorizedFields.filter(f => evalCondition(f, answers, { rowData: context }));
              if (visibleFieldsInCat.length === 0) return null;
 
              // 1. Identify Branching Triggers
              const branchingTriggers = categorizedFields.filter(
                (f) =>
                  (f.fieldType === "number" || f.fieldType === "multiselect") &&
                  categorizedFields.some((child) =>
                    child.displayCondition?.some(
                      (c) =>
                        (c.triggerQuestionId || c.triggerFieldId) === f.fieldId &&
                        c.operator === "exists"
                    )
                  )
              );
 
              // 2. Identify all dependents
              const allDependents = new Set();
              const dependentsMap = new Map();
 
              branchingTriggers.forEach((bt) => {
                const deps = categorizedFields.filter((child) =>
                  child.displayCondition?.some(
                    (c) =>
                      (c.triggerQuestionId || c.triggerFieldId) === bt.fieldId &&
                      c.operator === "exists"
                  )
                );
                dependentsMap.set(bt.fieldId, deps);
                deps.forEach((d) => allDependents.add(d.fieldId));
              });
 
              // 3. Render Fields
              const renderedFields = [];
              visibleFieldsInCat.forEach((field) => {
                if (allDependents.has(field.fieldId)) return; // Rendered inside loop
 
                // Render Normal Field
                renderedFields.push(
                  <div key={field.fieldId}>
                    <FieldTitle fieldTitle={field.fieldTitle} />
                    <GuidanceBlock guidance={field.guidance} />
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.fieldLabel}
                    </label>
                    <QuestionInput
                      field={field}
                      value={row.values[field.fieldId]}
                      disabled={[
                        "total_annual_consumption",
                        "avg_annual_consumption",
                      ].includes(field.fieldId)}
                      onChange={(val) =>
                        setTableRow(catKey, row.rowId, {
                          ...row.values,
                          [field.fieldId]: val,
                        })
                      }
                    />
                  </div>
                );
 
                // If it's a branching trigger, render its children loops right after it
                if (branchingTriggers.includes(field)) {
                  const rawValue = row.values[field.fieldId];
                  const isMultiselect = field.fieldType === "multiselect";
                  const iterations = isMultiselect
                    ? Array.isArray(rawValue)
                      ? rawValue
                      : []
                    : Array.from(
                      { length: Math.min(parseInt(rawValue) || 0, 100) },
                      (_, i) => i,
                    );
 
                  if (iterations.length > 0) {
                    const deps = dependentsMap.get(field.fieldId) || [];
                    renderedFields.push(
                      <div
                        key={`${field.fieldId}_loops`}
                        className="mt-4 space-y-4"
                      >
                        {iterations.map((iterVal, i) => {
                          const displayLabel = isMultiselect
                            ? (field.options?.find(o => o.value === iterVal)?.label || iterVal)
                            : `Entry ${i + 1}`;
 
                          return (
                            <div
                              key={`${field.fieldId}_loop_${i}`}
                              className="p-4 border border-teal-200 rounded-lg bg-teal-50/30 space-y-4"
                            >
                              <div className="text-teal-700 font-bold mb-2 uppercase text-xs tracking-wider">
                                {displayLabel}
                              </div>
                              {deps
                                .filter((depField) =>
                                  evalCondition(depField, answers, {
                                    rowData: context,
                                    tripIndex: i,
                                  }),
                                )
                                .map((depField) => {
                                  const suffixedId = `${depField.fieldId}${TRIP_SEP}${i}`;
                                  return (
                                    <div key={suffixedId}>
                                      <FieldTitle
                                        fieldTitle={depField.fieldTitle}
                                      />
                                      <GuidanceBlock
                                        guidance={depField.guidance}
                                      />
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {depField.fieldLabel}
                                      </label>
                                      <QuestionInput
                                        field={depField}
                                        value={row.values[suffixedId]}
                                        onChange={(val) =>
                                          setTableRow(catKey, row.rowId, {
                                            ...row.values,
                                            [suffixedId]: val,
                                          })
                                        }
                                      />
                                    </div>
                                  );
                                })}
                            </div>
                          );
                        })}
                      </div>,
                    );
                  }
                }
              });
 
              return (
                <div key={row.rowId} className="space-y-4">
                  {renderedFields}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
 
export default QuestionTable;